// New-supply land price calculation — MVP.
//
// Per logic/CALCULATION_MVP.md §12, this is the main entry point for
// MVP. It iterates the building items, runs per-item calculation, and
// aggregates to parcel-level totals.

import type { CalculationResult } from "../types/calculation-result.js";
import type { BuildingItem } from "../types/building-item.js";
import type { MarketPriceSet } from "../types/market-price.js";
import type { Parcel } from "../types/parcel.js";

import { calculateItem } from "./item-calculator.js";
import { MessageSink } from "./message-sink.js";
import { validateParcel } from "./validation.js";

export interface CalculateNewSupplyInput {
  readonly parcel: Parcel;
  readonly prices: MarketPriceSet;
  readonly items: BuildingItem[];
}

export function calculateNewSupplyLandPrice({
  parcel,
  prices,
  items,
}: CalculateNewSupplyInput): CalculationResult {
  const sink = new MessageSink();

  // 1. Per-item calculation.
  const itemResults = items.map((item) =>
    calculateItem({ item, parcel, prices, sink }),
  );

  // 2. Parcel-level validation.
  validateParcel(parcel, prices, items, sink);

  // 3. Aggregate.
  const totalLandPrice = itemResults.reduce(
    (sum, r) => sum + r.itemLandPrice,
    0,
  );
  const totalChargeableGfa = itemResults.reduce(
    (sum, r) => sum + r.chargeableGfa,
    0,
  );
  const averageFloorLandPrice =
    totalChargeableGfa > 0 ? totalLandPrice / totalChargeableGfa : 0;

  return {
    itemResults,
    totalLandPrice,
    totalChargeableGfa,
    averageFloorLandPrice,
    validationMessages: sink.toArray(),
  };
}
